/**
 * Is the person signed in Railit staff?
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS IS NOT `RequireRole`
 *
 * RequireRole tests `user.role`, which is the role inside ONE organization and
 * comes off the Membership row the access token names. Being platform staff is
 * not a role in a company: it is a property of the person, it survives
 * switching workspaces, and there is no value of `role` that could express it.
 * A guard written as `allow={['Owner']}` would let every customer's owner into
 * a console about every other customer.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE FLAG IS `platform_admin` ON THE WIRE
 *
 * The column is `is_platform_admin`. Login, refresh, switch-org and the
 * session read all rename it to `platform_admin` on the way out, and that is
 * the only spelling any of this code has ever seen. Reading the column's name
 * here returns undefined for everybody, which does not throw and does not warn:
 * it silently locks staff out of their own console.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT WRAPS THE LAYOUT. IT MUST NEVER SIT INSIDE ONE
 *
 * React Router renders a layout route's element BEFORE the matched child's, so
 *
 *     <Route element={<AdminLayout />}>
 *       <Route index element={<RequirePlatform>…</RequirePlatform>} />
 *
 * paints the entire staff navigation, every section heading and every page
 * title for any signed-in customer who types /admin, and only the innermost
 * panel says no. The two orderings look identical in a diff. See routes.tsx.
 *
 * This is the second gate either way: `/api/platform` refuses anybody without
 * the flag, so nothing is protected by this component alone. What it prevents
 * is a customer being shown a console shell and a row of failed requests.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
/* The shadcn button rather than @/components/ui/Button: that adapter renders
   `{leadingIcon}{children}{trailingIcon}`, so `asChild` hands radix's Slot three
   children and it throws. Every asChild caller in this app does the same. */
import { Button } from '@/components/shadcn/button';

export function RequirePlatform({ children }: { children: ReactNode }) {
  const { user } = useAuth();

  if (!user?.platform_admin) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <p className="text-[15px] font-medium text-foreground">This is for Railit staff.</p>
        <p className="max-w-[42ch] text-[13px] text-muted-foreground">
          Nothing about your own company lives here. If you were sent this link by mistake, your
          workspace is where you left it.
        </p>
        <Button variant="outline" size="sm" asChild>
          <Link to="/dashboard">Back to the app</Link>
        </Button>
      </div>
    );
  }

  return <>{children}</>;
}
